import type { ComponentType } from "react";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import {
  AdminPanelSettingsOutlined,
  AnalyticsOutlined,
  ApiOutlined,
  AppsOutlined,
  ArchitectureOutlined,
  AssignmentOutlined,
  HomeOutlined,
  AssessmentOutlined,
  BusinessOutlined,
  CardMembershipOutlined,
  CategoryOutlined,
  DeveloperModeOutlined,
  DescriptionOutlined,
  ErrorOutline,
  GroupOutlined,
  HubOutlined,
  InsightsOutlined,
  InventoryOutlined,
  LinkOutlined,
  ManageSearchOutlined,
  MenuBookOutlined,
  QuestionAnswerOutlined,
  RouterOutlined,
  SettingsOutlined,
  SpaceDashboardOutlined,
  SupportAgentOutlined,
  CloudQueueOutlined,
  RocketLaunchOutlined,
  VpnKeyOutlined,
} from "@mui/icons-material";
import { AiStarIcon } from "../components/AiStarIcon";
import { CATALOG_ASSISTANT_ROUTE } from "./agentDashboardConfig";
import {
  canAccessDevopsWorkspace,
  isArchitect,
  isCsr,
  isGatewayDeveloper,
  isProductManager,
  isSuperAdmin,
  isTenantAdmin,
} from "./appRoles";
import { PM_DASHBOARD_PATH, PM_HUBS } from "./pmHubRoutes";
import {
  PLATFORM_DASHBOARD_PATH,
  platformDashboardSectionPath,
} from "./platformDashboardRoutes";
import {
  checkRouteAccess,
  navFlagsToAccessKind,
  normalizePathname,
  type NavAccessFlags,
  type RouteAccessContext,
} from "./routeAccess";

export type WorkspaceId =
  | "developer"
  | "architect"
  | "product-manager"
  | "gateway"
  | "production-support"
  | "devops"
  | "org-admin"
  | "platform";

export const DEFAULT_WORKSPACE_ID: WorkspaceId = "developer";

const WORKSPACE_STORAGE_PREFIX = "sami.activeWorkspace.";

type NavItemMeta = {
  hidden?: boolean;
  /** When true, active/ownership matching is exact path only. */
  exact?: boolean;
};

export type NavIconComponent = ComponentType<SvgIconProps>;

export type NavLeaf = NavAccessFlags &
  NavItemMeta & {
    kind: "leaf";
    label: string;
    path: string;
    icon: NavIconComponent;
  };

export type NavGroup = NavAccessFlags &
  NavItemMeta & {
    kind: "group";
    label: string;
    icon: NavIconComponent;
    children: Omit<NavLeaf, "kind">[];
  };

export type NavEntry = NavLeaf | NavGroup;

export type WorkspaceDefinition = {
  id: WorkspaceId;
  label: string;
  description: string;
  icon: NavIconComponent;
  homePath: string;
  canAccess: (ctx: RouteAccessContext) => boolean;
  navItems: NavEntry[];
};

export const ALWAYS_VISIBLE_NAV: NavLeaf[] = [
  { kind: "leaf", label: "Home", path: "/", icon: HomeOutlined, exact: true },
];

export const SUPPORT_NAV: NavLeaf = {
  kind: "leaf",
  label: "Support",
  path: "/support-cases",
  icon: SupportAgentOutlined,
  access: "auth",
};

function signedIn(ctx: RouteAccessContext): boolean {
  return ctx.hasToken;
}

export const WORKSPACES: WorkspaceDefinition[] = [
  {
    id: "developer",
    label: "Developer",
    description: "Explore APIs, products, events, and subscriptions.",
    icon: DeveloperModeOutlined,
    homePath: "/product-explorer",
    canAccess: signedIn,
    navItems: [
      {
        kind: "leaf",
        label: "Product Explorer",
        path: "/product-explorer",
        icon: AppsOutlined,
        access: "auth",
      },
      {
        kind: "leaf",
        label: "API Explorer",
        path: "/api-explorer",
        icon: ApiOutlined,
        access: "auth",
      },
      {
        kind: "leaf",
        label: "Kafka Event Explorer",
        path: "/kafka-event-explorer",
        icon: HubOutlined,
        access: "auth",
      },
      {
        kind: "leaf",
        label: "Category Explorer",
        path: "/category-explorer",
        icon: CategoryOutlined,
        access: "auth",
      },
      {
        kind: "leaf",
        label: "My Subscriptions",
        path: "/my-subscriptions",
        icon: CardMembershipOutlined,
        access: "auth",
      },
      {
        kind: "leaf",
        label: "Integration Support",
        path: "/agent-dashboard/integration-support",
        icon: AiStarIcon,
        access: "integrationSupport",
      },
    ],
  },
  {
    id: "architect",
    label: "Architect",
    description: "Design APIs, manage architecture standards, and review events and gateway routes.",
    icon: ArchitectureOutlined,
    homePath: "/agent-dashboard/architect",
    canAccess: (ctx) => isArchitect(ctx.roles),
    navItems: [
      {
        kind: "leaf",
        label: "Architect Agent",
        path: "/agent-dashboard/architect",
        icon: ArchitectureOutlined,
        access: "architect",
      },
      {
        kind: "leaf",
        label: "Intake Assistant",
        path: "/agent-dashboard/architect/intake",
        icon: AssignmentOutlined,
        access: "architect",
      },
      {
        kind: "leaf",
        label: "Architecture Standards",
        path: PM_HUBS.architectureStandards,
        icon: DescriptionOutlined,
        access: "architect",
      },
      {
        kind: "leaf",
        label: "API Explorer",
        path: "/api-explorer",
        icon: ApiOutlined,
        access: "auth",
      },
      {
        kind: "leaf",
        label: "API Hub",
        path: PM_HUBS.api,
        icon: ApiOutlined,
        access: "apiHub",
      },
      {
        kind: "leaf",
        label: "Kafka Event Registry",
        path: PM_HUBS.kafkaEvent,
        icon: HubOutlined,
        access: "kafkaEventHub",
      },
      {
        kind: "leaf",
        label: "Gateway Route Hub",
        path: PM_HUBS.gatewayRoute,
        icon: RouterOutlined,
        access: "gatewayRouteHub",
      },
    ],
  },
  {
    id: "product-manager",
    label: "Product Manager",
    description: "Manage catalog entities, gateway SKUs, and insights.",
    icon: SpaceDashboardOutlined,
    homePath: PM_DASHBOARD_PATH,
    canAccess: (ctx) => isProductManager(ctx.roles),
    navItems: [
      {
        kind: "leaf",
        label: "Dashboard",
        path: PM_DASHBOARD_PATH,
        icon: SpaceDashboardOutlined,
        access: "productManager",
        exact: true,
      },
      {
        kind: "group",
        label: "Catalog",
        icon: InventoryOutlined,
        children: [
          {
            label: "Products",
            path: PM_HUBS.product,
            icon: InventoryOutlined,
            access: "productManager",
          },
          {
            label: "Categories",
            path: PM_HUBS.category,
            icon: CategoryOutlined,
            access: "productManager",
          },
          {
            label: "APIs",
            path: PM_HUBS.api,
            icon: ApiOutlined,
            access: "apiHub",
          },
          {
            label: "FAQs",
            path: PM_HUBS.faq,
            icon: QuestionAnswerOutlined,
            access: "productManager",
          },
          {
            label: "Links",
            path: PM_HUBS.link,
            icon: LinkOutlined,
            access: "productManager",
          },
          {
            label: "Documents",
            path: PM_HUBS.document,
            icon: DescriptionOutlined,
            access: "productManager",
          },
          {
            label: "Knowledge Search",
            path: PM_HUBS.knowledgeSearch,
            icon: ManageSearchOutlined,
            access: "documentHub",
          },
          {
            label: "Developer Guides",
            path: PM_HUBS.developerGuide,
            icon: MenuBookOutlined,
            access: "productManager",
          },
          {
            label: "Management Groups",
            path: PM_HUBS.productManagementGroup,
            icon: GroupOutlined,
            access: "productManager",
          },
          {
            label: "Operational Errors",
            path: PM_HUBS.operationalError,
            icon: ErrorOutline,
            access: "productManager",
          },
        ],
      },
      {
        kind: "group",
        label: "Gateway",
        icon: RouterOutlined,
        children: [
          {
            label: "Gateway SKUs",
            path: PM_HUBS.gatewaySku,
            icon: RouterOutlined,
            access: "productManager",
          },
          {
            label: "App KYC",
            path: PM_HUBS.appKyc,
            icon: VpnKeyOutlined,
            access: "productManager",
          },
        ],
      },
      {
        kind: "group",
        label: "Insights",
        icon: InsightsOutlined,
        children: [
          {
            label: "Analytics",
            path: "/analytics-dashboard",
            icon: AnalyticsOutlined,
            access: "productManager",
            exact: true,
          },
          {
            label: "Portfolio Intelligence",
            path: PM_HUBS.apiPortfolioIntelligence,
            icon: AssessmentOutlined,
            access: "productManager",
          },
        ],
      },
      {
        kind: "leaf",
        label: "Catalog Assistant",
        path: CATALOG_ASSISTANT_ROUTE,
        icon: AiStarIcon,
        access: "productManager",
      },
      {
        kind: "leaf",
        label: "Subscription Requests",
        path: "/subscription-requests",
        icon: CardMembershipOutlined,
        access: "productManager",
      },
    ],
  },
  {
    id: "gateway",
    label: "Gateway",
    description: "Manage confidential gateway routes and upstreams.",
    icon: RouterOutlined,
    homePath: PM_HUBS.gatewayRoute,
    canAccess: (ctx) => isGatewayDeveloper(ctx.roles),
    navItems: [
      {
        kind: "leaf",
        label: "Gateway Route Hub",
        path: PM_HUBS.gatewayRoute,
        icon: RouterOutlined,
        access: "gatewayRouteHub",
      },
    ],
  },
  {
    id: "production-support",
    label: "Production Support",
    description: "Triage production errors with AI-assisted operator tools.",
    icon: SupportAgentOutlined,
    homePath: "/agent-dashboard/production-support",
    canAccess: (ctx) => isCsr(ctx.roles),
    navItems: [
      {
        kind: "leaf",
        label: "Operator Hub",
        path: "/agent-dashboard/production-support",
        icon: SupportAgentOutlined,
        access: "productionSupport",
      },
      {
        kind: "leaf",
        label: "Operator documents",
        path: PM_HUBS.document,
        icon: DescriptionOutlined,
        access: "documentHub",
      },
      {
        kind: "leaf",
        label: "Knowledge Search",
        path: PM_HUBS.knowledgeSearch,
        icon: ManageSearchOutlined,
        access: "documentHub",
      },
      {
        kind: "leaf",
        label: "Operational errors",
        path: PM_HUBS.operationalError,
        icon: ErrorOutline,
        access: "operationalErrorHub",
      },
    ],
  },
  {
    id: "devops",
    label: "DevOps / Environments",
    description: "Environments, applications, configuration, and releases.",
    icon: CloudQueueOutlined,
    homePath: "/agent-dashboard/devops",
    canAccess: (ctx) => canAccessDevopsWorkspace(ctx.roles),
    navItems: [
      {
        kind: "leaf",
        label: "DevOps Agent",
        path: "/agent-dashboard/devops",
        icon: CloudQueueOutlined,
        access: "devops",
      },
      {
        kind: "leaf",
        label: "Environments",
        path: "/devops/environments",
        icon: CloudQueueOutlined,
        access: "devops",
      },
      {
        kind: "leaf",
        label: "Applications",
        path: "/devops/applications",
        icon: AppsOutlined,
        access: "devops",
      },
      {
        kind: "leaf",
        label: "Configurations",
        path: "/devops/configurations",
        icon: DescriptionOutlined,
        access: "devops",
      },
      {
        kind: "leaf",
        label: "Releases",
        path: "/devops/releases",
        icon: RocketLaunchOutlined,
        access: "devops",
      },
    ],
  },
  {
    id: "org-admin",
    label: "Org Admin",
    description: "Organization connectors and tenant settings.",
    icon: SettingsOutlined,
    homePath: "/settings",
    canAccess: (ctx) => isTenantAdmin(ctx.roles),
    navItems: [
      {
        kind: "leaf",
        label: "Connectors",
        path: "/connectors-dashboard",
        icon: HubOutlined,
        access: "tenantAdmin",
      },
      {
        kind: "leaf",
        label: "Settings",
        path: "/settings",
        icon: SettingsOutlined,
        access: "tenantAdmin",
        exact: true,
      },
    ],
  },
  {
    id: "platform",
    label: "Platform",
    description: "Platform-wide tenant management, insights, and agent-runner bindings.",
    icon: AdminPanelSettingsOutlined,
    homePath: PLATFORM_DASHBOARD_PATH,
    canAccess: (ctx) => isSuperAdmin(ctx.roles),
    navItems: [
      {
        kind: "leaf",
        label: "Dashboard",
        path: PLATFORM_DASHBOARD_PATH,
        icon: AdminPanelSettingsOutlined,
        access: "superAdmin",
        exact: true,
      },
      {
        kind: "leaf",
        label: "Tenant Management",
        path: platformDashboardSectionPath("tenant-management"),
        icon: BusinessOutlined,
        access: "superAdmin",
      },
      {
        kind: "leaf",
        label: "Tenant Insights",
        path: platformDashboardSectionPath("tenant-insights"),
        icon: InsightsOutlined,
        access: "superAdmin",
      },
      {
        kind: "leaf",
        label: "Agent runners",
        path: platformDashboardSectionPath("agent-runners"),
        icon: HubOutlined,
        access: "superAdmin",
      },
    ],
  },
];

export function isWorkspaceId(value: string | null | undefined): value is WorkspaceId {
  return WORKSPACES.some((ws) => ws.id === value);
}

export function getWorkspaceById(id: WorkspaceId): WorkspaceDefinition | undefined {
  return WORKSPACES.find((ws) => ws.id === id);
}

export function getAccessibleWorkspaces(
  ctx: RouteAccessContext,
): WorkspaceDefinition[] {
  if (!ctx.hasToken) return [];
  return WORKSPACES.filter((ws) => ws.canAccess(ctx));
}

export function flattenNavLeaves(entries: NavEntry[]): NavLeaf[] {
  return entries.flatMap((entry) => {
    if (entry.kind === "leaf") return [entry];
    return entry.children.map((child) => ({ ...child, kind: "leaf" as const }));
  });
}

export function navPathMatches(
  pathname: string,
  navPath: string,
  exact?: boolean,
): boolean {
  const normalized = normalizePathname(pathname);
  if (navPath === "/" || exact) {
    return normalized === navPath;
  }
  return normalized === navPath || normalized.startsWith(`${navPath}/`);
}

export function workspaceOwnsPath(
  workspace: WorkspaceDefinition,
  pathname: string,
): boolean {
  return flattenNavLeaves(workspace.navItems).some((leaf) =>
    navPathMatches(pathname, leaf.path, leaf.exact),
  );
}

export function workspaceMatchLength(
  workspace: WorkspaceDefinition,
  pathname: string,
): number {
  let best = 0;
  for (const leaf of flattenNavLeaves(workspace.navItems)) {
    if (navPathMatches(pathname, leaf.path, leaf.exact) && leaf.path.length > best) {
      best = leaf.path.length;
    }
  }
  return best;
}

export function isWorkspaceChromePath(pathname: string): boolean {
  const normalized = normalizePathname(pathname);
  if (normalized === "/" || normalized === "/profile") return true;
  return (
    normalized === "/support-cases" ||
    normalized.startsWith("/support-cases/")
  );
}

export function filterNavEntries(
  entries: NavEntry[],
  ctx: RouteAccessContext,
): NavEntry[] {
  return entries.flatMap((entry): NavEntry[] => {
    if (entry.hidden) return [];
    if (entry.kind === "leaf") {
      return checkRouteAccess(navFlagsToAccessKind(entry), ctx) ? [entry] : [];
    }
    if (!checkRouteAccess(navFlagsToAccessKind(entry), ctx)) return [];
    const children = entry.children.filter(
      (child) =>
        !child.hidden &&
        checkRouteAccess(navFlagsToAccessKind(child), ctx),
    );
    if (children.length === 0) return [];
    return [{ ...entry, children }];
  });
}

function storageKey(tenantId: string | null | undefined): string {
  return `${WORKSPACE_STORAGE_PREFIX}${tenantId || "default"}`;
}

export function readStoredWorkspaceId(
  tenantId: string | null | undefined,
): WorkspaceId | null {
  try {
    const raw = localStorage.getItem(storageKey(tenantId));
    return isWorkspaceId(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function writeStoredWorkspaceId(
  tenantId: string | null | undefined,
  id: WorkspaceId,
): void {
  try {
    localStorage.setItem(storageKey(tenantId), id);
  } catch {
    // Ignore quota / private-mode failures.
  }
}

/**
 * Pick the active workspace: URL wins when it belongs to an accessible
 * workspace; otherwise restore the stored choice or fall back to Developer.
 */
export function resolveActiveWorkspace(
  pathname: string,
  available: WorkspaceDefinition[],
  storedId: WorkspaceId | null,
): WorkspaceDefinition | null {
  if (available.length === 0) return null;

  const stored =
    storedId != null
      ? available.find((ws) => ws.id === storedId) ?? null
      : null;
  const fallback: WorkspaceDefinition =
    stored ??
    available.find((ws) => ws.id === DEFAULT_WORKSPACE_ID) ??
    available[0];

  if (isWorkspaceChromePath(pathname)) {
    return fallback;
  }

  const matching = available
    .map((ws) => ({ ws, length: workspaceMatchLength(ws, pathname) }))
    .filter((row) => row.length > 0)
    .sort((a, b) => b.length - a.length);

  if (matching.length === 0) {
    return fallback;
  }

  if (stored && matching.some((row) => row.ws.id === stored.id)) {
    return stored;
  }

  return matching[0].ws;
}
